import '../../components.scss';
// eslint-disable-next-line import/no-unresolved -- leaflet.heat main is dist/leaflet-heat.js, resolved by bundler
import 'leaflet.heat';

import { Method } from '_lib/api';
import apiRoute from '_lib/api/paths';
import { getFetchHeaders } from '_lib/api/utils';
import { RootState, useAppDispatch } from '_store';
import { getTopologyType } from '_store/application/hooks';
import { pmvL10nArgs } from '_store/application/pmvLabels';
import applicationReducer, {
  setHeatmapSlotDataAction,
  setHeatmapTimeExtentAction,
} from '_store/application/reducer';
import { heatmapSelector, themeSelector } from '_store/application/selectors';
import { AppTheme, DialogType, TopologyType } from '_store/application/types';
import { useDenmsRequest } from '_store/denm/utils';
import { devicesSagas } from '_store/devices/sagas';
import { useIvimsRequest } from '_store/ivim/utils';
import { pmvsSagas } from '_store/pmv/sagas';
import roadsReducer from '_store/roads/roadsReducers';
import { selectedRoadSegmentIdsSelector } from '_store/roads/selectors';
import { spatemsSagas } from '_store/spatem/spatemsSagas';
import { useLocalization } from '@fluent/react';
import { faCircleHalfStroke } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@material-ui/core';
import { RouteComponentProps } from '@reach/router';
import L from 'leaflet';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { MapContainer, Polyline } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { useSelector } from 'react-redux';

import { getFromLocalStorage, updateLocalStorage } from '../../../../utils';
import { UnifiedMarkerCluster } from './clustering';
import Denms from './Elements/Denms';
import Devices from './Elements/Devices';
import ExtraSelectionElements from './Elements/ExtraSelectionElements';
import Ivims from './Elements/Ivims';
import Pmvs from './Elements/Pmvs';
import RenderPath from './Elements/renderPath';
import Roads from './Elements/Roads';
import SelectionElements from './Elements/SelectionElements';
import Spatems from './Elements/Spatems';
import Vehicles from './Elements/Vehicles';
import MapEvents from './Events/MapEvents';
import {
  gridApiResponseToHeatmapPoints,
  gridApiResponseToSlotData,
  gridApiResponseToSpeedValueRange,
  GridCamApiResponse,
  HeatmapPoint,
} from './heatmapCams';
import useMap from './hooks/useMap';
import { darkModeStyle, lightModeStyle } from './mapStyles';
import styles from './styles';
import { toLatLng } from './utils';

const enum MapType {
  ROADMAP = 'roadmap',
  HYBRID = 'hybrid',
  TERRAIN = 'terrain',
  SATELLITE = 'satellite',
}

const isUrbanTopology = getTopologyType() === TopologyType.URBAN;
const layers = ['event', 'informative', 'rsu', 'pmv', ...(isUrbanTopology ? ['spatem'] : []), 'data'];

const GOOGLE_MAPS_CALLBACK = '__googleMapsCallback_emeras';

/** Same blue → lime → red mapping as the heat legend / leaflet.heat gradient. */
function speedToHeatColor(t: number): string {
  const clamp = Math.max(0, Math.min(1, t));
  const blue = [0, 0, 255];
  const lime = [0, 255, 0];
  const red = [255, 0, 0];
  const lerp = (a: number[], b: number[], u: number) =>
    a.map((v, i) => Math.round(v + (b[i] - v) * u));
  let rgb: number[];
  if (clamp <= 0.4) rgb = blue;
  else if (clamp <= 0.65) rgb = lerp(blue, lime, (clamp - 0.4) / 0.25);
  else rgb = lerp(lime, red, (clamp - 0.65) / 0.35);
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

/**
 * Single-canvas speed overlay (avoids thousands of CircleMarker DOM/SVG nodes).
 * Color = average speed; radius uses power curve (more small, fewer large).
 */
function createSpeedCirclesLayer(
  points: HeatmapPoint[],
  scaleMin: number,
  scaleMax: number,
): L.Layer {
  const span = Math.max(scaleMax - scaleMin, 1e-6);
  const minRadius = 2;
  const maxRadius = 64;
  const minOpacity = 0.2;
  const maxOpacity = 1;
  // High speeds first so smaller (slower) circles are drawn on top
  const sortedPoints = points
    .filter(([, , speed]) => speed > 0)
    .sort((a, b) => b[2] - a[2]);

  const SpeedCirclesLayer = L.Layer.extend({
    onAdd(map: L.Map) {
      this._map = map;
      this._canvas = L.DomUtil.create('canvas', 'leaflet-layer');
      this._canvas.style.pointerEvents = 'none';
      map.getPanes().overlayPane.appendChild(this._canvas);
      map.on('move zoom resize', this._redraw, this);
      this._redraw();
    },

    onRemove(map: L.Map) {
      L.DomUtil.remove(this._canvas);
      map.off('move zoom resize', this._redraw, this);
    },

    _redraw() {
      const map = this._map as L.Map;
      const canvas = this._canvas as HTMLCanvasElement;
      const size = map.getSize();
      const topLeft = map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(canvas, topLeft);
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(size.x * dpr);
      canvas.height = Math.round(size.y * dpr);
      canvas.style.width = `${size.x}px`;
      canvas.style.height = `${size.y}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size.x, size.y);
      for (let i = 0; i < sortedPoints.length; i += 1) {
        const [lat, lng, speed] = sortedPoints[i];
        const t = Math.max(0, Math.min(1, (speed - scaleMin) / span));
        // Same power curve as radius: lower speeds stay smaller and more transparent
        const tScaled = t ** 2.5;
        const radius = minRadius + tScaled * (maxRadius - minRadius);
        const opacity = minOpacity + tScaled * (maxOpacity - minOpacity);
        const p = map.latLngToContainerPoint([lat, lng]);
        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = speedToHeatColor(t);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    },
  });

  return new SpeedCirclesLayer();
}

const MapView: FC = (_props: RouteComponentProps): ReactElement => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const theme = useSelector(themeSelector);
  const heatmap = useSelector(heatmapSelector);
  const [apiGridResponse, setApiGridResponse] = useState<GridCamApiResponse | null>(null);
  const [heatmapGridLoading, setHeatmapGridLoading] = useState(false);
  const [heatmapGridError, setHeatmapGridError] = useState<Error | null>(null);

  // API called when base range or selected metric changes; only the endpoint for the selected metric is fetched
  const baseRangeStart = heatmap.baseRange[0];
  const baseRangeEnd = heatmap.baseRange[1];
  const activeMetric = heatmap.active;
  const dateRangeStart = heatmap.dateRange[0];
  const dateRangeEnd = heatmap.dateRange[1];
  useEffect(() => {
    const minTs = baseRangeStart;
    const maxTs = baseRangeEnd;
    if (minTs >= maxTs || activeMetric === 'inactive') {
      setApiGridResponse(null);
      return undefined;
    }
    const startIso = new Date(minTs).toISOString();
    const endIso = new Date(maxTs).toISOString();
    const path =
      activeMetric === 'counter'
        ? apiRoute.camsGridVehicleCounts(startIso, endIso)
        : apiRoute.camsGridSpeedValues(startIso, endIso);
    const hostname = process.env.API_ENDPOINT;
    if (!hostname) {
      setHeatmapGridError(new Error('API_ENDPOINT not configured'));
      return undefined;
    }
    let cancelled = false;
    setHeatmapGridLoading(true);
    setHeatmapGridError(null);
    fetch(`${hostname}/${path}`, {
      method: Method.GET,
      headers: getFetchHeaders({ authenticated: true, method: Method.GET }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Heatmap API ${res.status}`);
        return res.json() as Promise<GridCamApiResponse>;
      })
      .then((data: GridCamApiResponse) => {
        if (cancelled) return;
        setApiGridResponse(data);
        dispatch(setHeatmapTimeExtentAction([minTs, maxTs]));
        dispatch(setHeatmapSlotDataAction(gridApiResponseToSlotData(data, activeMetric)));
      })
      .catch((err) => {
        if (!cancelled) {
          setHeatmapGridError(err instanceof Error ? err : new Error(String(err)));
          setApiGridResponse(null);
          // eslint-disable-next-line no-console -- surface API failure
          console.error('[heatmap] grid API failed', err);
        }
      })
      .finally(() => {
        if (!cancelled) setHeatmapGridLoading(false);
      });
    return () => {
      cancelled = true;
      return undefined;
    };
  }, [baseRangeStart, baseRangeEnd, activeMetric, dispatch]);

  const heatmapPoints = useMemo((): HeatmapPoint[] => {
    if (
      (heatmap.active !== 'counter' && heatmap.active !== 'speed_value') ||
      !apiGridResponse ||
      dateRangeStart >= dateRangeEnd
    ) {
      return [];
    }
    return gridApiResponseToHeatmapPoints(apiGridResponse, heatmap.active, [
      dateRangeStart,
      dateRangeEnd,
    ]);
  }, [heatmap.active, apiGridResponse, dateRangeStart, dateRangeEnd]);

  // Full baseRange aggregation — used for stable color scale by default
  const fullHeatmapPoints = useMemo((): HeatmapPoint[] => {
    if (
      (heatmap.active !== 'counter' && heatmap.active !== 'speed_value') ||
      !apiGridResponse
    ) {
      return [];
    }
    return gridApiResponseToHeatmapPoints(apiGridResponse, heatmap.active);
  }, [heatmap.active, apiGridResponse]);

  const heatmapValueRange = useMemo((): { min: number; max: number } | null => {
    // Speed: min/max of individual cell×quarter-hour averages (km/h) — never a time/cell sum
    if (heatmap.active === 'speed_value') {
      if (!apiGridResponse) return null;
      return gridApiResponseToSpeedValueRange(
        apiGridResponse,
        heatmap.scaleToVisibleData && dateRangeStart < dateRangeEnd
          ? [dateRangeStart, dateRangeEnd]
          : undefined,
      );
    }
    const scalePoints = heatmap.scaleToVisibleData ? heatmapPoints : fullHeatmapPoints;
    if (scalePoints.length === 0) return null;
    let min = scalePoints[0][2];
    let max = scalePoints[0][2];
    for (let i = 1; i < scalePoints.length; i += 1) {
      const v = scalePoints[i][2];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    return { min, max };
  }, [
    heatmap.active,
    heatmap.scaleToVisibleData,
    apiGridResponse,
    dateRangeStart,
    dateRangeEnd,
    heatmapPoints,
    fullHeatmapPoints,
  ]);

  // Check if the heatmap feature is enabled via environment variable
  const showHeatmap = true; // process.env.SHOW_HEATMAP === 'true';

  const mapTypeFromLocalStorage = useMemo(
    () => (getFromLocalStorage('mapType') as MapType) ?? MapType.ROADMAP,
    [],
  );

  const [mapType, setMapType] = useState(mapTypeFromLocalStorage);
  const updateMapType = (e) => {
    updateLocalStorage('mapType', e.target.value);
    setMapType(e.target.value);
  };

  const updateTheme = (e) => {
    const newTheme = e.target.checked ? AppTheme.DARK : AppTheme.LIGHT;
    updateLocalStorage('appTheme', newTheme);
    dispatch(
      applicationReducer.actions.setTheme({
        theme: newTheme,
      }),
    );
  };

  const {
    roads,
    devices,
    pmvs,
    denms,
    ivim,
    vehicles,
    spatems,
    dialog,
    extra,
    selection,
    activeLayers,
    mapRef,
    mapResizeKey,
    loading,
    links,
    loadHandler,
    meterPerPixel,
    centerCoordinates,
    googleMapsApiKey,
    zoom,
  } = useMap();

  // Load Google Maps API with loading=async (best practice); then the layer uses useGoogMapsLoader: false
  useEffect(() => {
    const w = typeof window !== 'undefined' ? window : null;
    const hasGoogle = w && (w as Window & { google?: { maps?: unknown } }).google?.maps;
    if (!googleMapsApiKey || hasGoogle) return;
    if (document.getElementById('google-maps-script-emeras')) return;
    const callbackName = GOOGLE_MAPS_CALLBACK;
    (window as unknown as Record<string, () => void>)[callbackName] = function onGoogleMapsLoaded() {
      (window as unknown as Record<string, () => void>)[callbackName] = function noop() {
        /* API loaded */
      };
    };
    const params = new URLSearchParams({
      key: googleMapsApiKey,
      loading: 'async',
      v: '3.64',
      language: 'it',
      region: 'IT',
      callback: callbackName,
    });
    const script = document.createElement('script');
    script.id = 'google-maps-script-emeras';
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [googleMapsApiKey]);

  const dispatchDenmsRequest = useDenmsRequest({
    currentPage: 1,
  });
  const dispatchIvimsRequest = useIvimsRequest({ currentPage: 1 });
  const selectedRoadSegmentIds = useSelector(selectedRoadSegmentIdsSelector);

  const denmFormState = useSelector((state: RootState) => state.forms['manage-denm']);

  const updateSelectedRoad = (e) => {
    const selectedRoads = e.target.value as string[];

    // Prevent deselection if there is only one road selected
    if (selectedRoads.length === 0 && selectedRoadSegmentIds.length === 1) {
      return;
    }

    updateLocalStorage('selectedRoadIds', JSON.stringify(selectedRoads));

    dispatch(roadsReducer.actions.selectRoad(selectedRoads));
    dispatch(devicesSagas.actions.devicesRequest({ roadSegmentIds: selectedRoads }));
    dispatch(pmvsSagas.actions.vmsRequest());
    if (isUrbanTopology) {
      dispatch(spatemsSagas.actions.spatemsRequest({ roadSegmentIds: selectedRoads }));
    }

    dispatchDenmsRequest(selectedRoads);
    dispatchIvimsRequest(selectedRoads);
  };

  const mapStyle = theme === AppTheme.DARK ? darkModeStyle : lightModeStyle;

  // Counter: Leaflet.heat (intensities stack — correct for density).
  // Speed: circle markers colored by absolute average km/h (heat would stack and look "accumulated").
  const HEATMAP_MAX_POINTS = 3000;
  const heatmapPointsCapped = useMemo((): HeatmapPoint[] => {
    if (heatmapPoints.length <= HEATMAP_MAX_POINTS) return heatmapPoints;
    const step = heatmapPoints.length / HEATMAP_MAX_POINTS;
    const out: HeatmapPoint[] = [];
    for (let i = 0; i < HEATMAP_MAX_POINTS; i += 1) {
      out.push(heatmapPoints[Math.floor(i * step)]);
    }
    return out;
  }, [heatmapPoints]);

  useEffect(() => {
    if (mapRef && showHeatmap && heatmap.active !== 'inactive' && heatmapPointsCapped.length > 0) {
      const scaleMin = heatmapValueRange?.min ?? 0;
      const scaleMax = Math.max(heatmapValueRange?.max ?? 1, scaleMin + 1e-6);

      if (heatmap.active === 'speed_value') {
        const layer = createSpeedCirclesLayer(heatmapPointsCapped, scaleMin, scaleMax);
        layer.addTo(mapRef);
        return () => {
          mapRef.removeLayer(layer);
        };
      }

      const heatOptions: {
        radius: number;
        blur: number;
        max: number;
        maxZoom: number;
        minOpacity: number;
        gradient: Record<number, string>;
      } = {
        radius: 25,
        blur: 15,
        max: Math.max(scaleMax, 1),
        maxZoom: 1, // zoom level where intensity is max; use 1 so colors stay stable when zooming
        minOpacity: 0.1,
        gradient: { 0.4: 'blue', 0.65: 'lime', 1: 'red' },
      };
      const LHeat = L as unknown as {
        heatLayer: (latlngs: [number, number, number][], options?: typeof heatOptions) => L.Layer;
      };
      const heatLayer = LHeat.heatLayer(heatmapPointsCapped, heatOptions);
      heatLayer.addTo(mapRef);

      return () => {
        mapRef.removeLayer(heatLayer);
      };
    }
    return undefined;
  }, [
    mapRef,
    mapResizeKey,
    showHeatmap,
    heatmap.active,
    heatmapPointsCapped,
    heatmapValueRange,
  ]);

  return (
    <Card className={classes.cardStyle}>
      <CardContent className={classes.cardContent}>
        {loading ? (
          <Typography>{l10n.getString('map-loading')}</Typography>
        ) : (
          <MapContainer
            className={classes.mapContainer}
            zoom={zoom}
            center={centerCoordinates}
            scrollWheelZoom={true}
            doubleClickZoom={false}
            attributionControl={false}
            whenCreated={loadHandler}
            // onClick={pointClicked}
          >
            <ReactLeafletGoogleLayer
              key={`${mapType}-${theme}`}
              type={mapType}
              continuousWorld={true}
              apiKey={googleMapsApiKey}
              useGoogMapsLoader={false}
              googleMapsLoaderConf={{
                version: '3.64',
                language: 'it',
                region: 'IT',
                apiKey: googleMapsApiKey,
              }}
              styles={mapStyle}
            />
            <div className={classes.mapButtons}>
              <ButtonGroup size="large" disableElevation variant="contained">
                {layers.map((layer) => (
                  <Button
                    key={layer}
                    className={activeLayers[layer] ? 'Map-buttons-sel' : 'Map-buttons'}
                    onClick={() => {
                      updateLocalStorage(layer, !activeLayers[layer]);
                      dispatch(applicationReducer.actions.toggleActiveLayer(layer));
                    }}
                  >
                    {layer === 'pmv'
                      ? l10n.getString('map-filter-pmv', pmvL10nArgs())
                      : l10n.getString(`map-filter-${layer}`)}
                  </Button>
                ))}
              </ButtonGroup>
              <div>
                <Select
                  className="Map-Road-select"
                  onChange={updateSelectedRoad}
                  value={selectedRoadSegmentIds ?? []}
                  multiple
                  variant="outlined"
                >
                  {roads.map((road, key) => (
                    <MenuItem key={key} value={road.id}>
                      {road.name}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  className="Map-Type-select"
                  onChange={updateMapType}
                  value={mapType}
                  variant="outlined"
                >
                  {[MapType.ROADMAP, MapType.HYBRID, MapType.TERRAIN, MapType.SATELLITE].map(
                    (type, key) => (
                      <MenuItem key={key} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)} map
                      </MenuItem>
                    ),
                  )}
                </Select>
              </div>
            </div>
            {mapRef && (
              <>
                {roads && <Roads roads={roads} />}
                <UnifiedMarkerCluster>
                  {devices && activeLayers.rsu && <Devices devices={devices} mapRef={mapRef} />}
                  {pmvs && activeLayers.pmv && <Pmvs pmvs={pmvs} mapRef={mapRef} />}
                  {spatems && activeLayers.spatem && <Spatems spatems={spatems} mapRef={mapRef} />}
                  {denms && activeLayers.event && <Denms denms={denms} mapRef={mapRef} />}
                  {ivim && activeLayers.informative && <Ivims ivims={ivim} mapRef={mapRef} />}
                </UnifiedMarkerCluster>
                {vehicles && activeLayers.data && <Vehicles vehicles={vehicles} mapRef={mapRef} />}
              </>
            )}

            {selection.style && <SelectionElements selection={selection} />}

            <ExtraSelectionElements extra={extra} />

            <MapEvents />

            {/* TODO: reimplement this stuff */}
            {mapRef &&
              Object.entries(links).map(([id, l]) => (
                <Polyline
                  key={`link-${id}`}
                  positions={l
                    .filter((n) => n.loc && n.loc.coordinates)
                    .map((n) => toLatLng(n.loc.coordinates))}
                  weight={1}
                  color="#0ff"
                  // geodesic={true}
                />
              ))}

            {dialog && mapRef && (
              <RenderPath
                dialog={dialog}
                denms={activeLayers.event && denms}
                ivim={activeLayers.informative && ivim}
                meterPerPixel={meterPerPixel}
                createDenmGeoNetworkingDistance={
                  dialog.type === DialogType.DENM_LIST &&
                  denmFormState?.optional?.geo_networking?.distance_a
                    ? denmFormState.optional.geo_networking.distance_a
                    : undefined
                }
                referencePoint={
                  dialog.type === DialogType.DENM_LIST && denmFormState?.referencePoint
                    ? denmFormState.referencePoint
                    : undefined
                }
              />
            )}
          </MapContainer>
        )}
        {showHeatmap && heatmap.active !== 'inactive' && heatmapGridLoading && !loading && (
          <div className={classes.heatmapLoadingOverlay} aria-label="Loading heatmap data">
            <CircularProgress size={48} />
          </div>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={theme === AppTheme.DARK}
              onChange={updateTheme}
              name="darkModeSwitch"
              color="primary"
              size="small"
            />
          }
          label={<FontAwesomeIcon icon={faCircleHalfStroke} size="sm" />}
          className={classes.themeSwitch}
        />

        {showHeatmap && heatmap.active !== 'inactive' && (
          <>
            {!heatmapGridLoading && heatmapGridError && (
              <Typography variant="caption" color="error" className={classes.heatmapLegend}>
                {heatmapGridError.message}
              </Typography>
            )}
            {!heatmapGridLoading &&
              !heatmapGridError &&
              heatmapPointsCapped.length > 0 &&
              heatmapValueRange && (
                <div className={classes.heatmapLegend}>
                  <Typography className={classes.heatmapLegendMax} variant="caption" component="span">
                    {heatmap.active === 'speed_value'
                      ? `${Math.round(heatmapValueRange.max)} km/h`
                      : heatmapValueRange.max.toLocaleString()}
                  </Typography>
                  <div className={classes.heatmapLegendGradient} aria-hidden />
                  <Typography className={classes.heatmapLegendMin} variant="caption" component="span">
                    {heatmap.active === 'speed_value'
                      ? `${Math.round(heatmapValueRange.min)} km/h`
                      : heatmapValueRange.min.toLocaleString()}
                  </Typography>
                </div>
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(MapView);
